import {
  CheckBox,
  FieldSet,
  FieldWrapper,
  FormattedString,
  FormContext,
  FormState,
  getOverlapCidrs,
  InfoBlockLayout,
  InfoBlockStatus,
  InputWizardPanelComponent,
  IpInput,
  RadioGroup,
  RadioGroupOption,
  SimpleBuilder,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import { mergeStyleSets } from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { AddressRange as AddressRangeDisplay } from "../../components/AddressRange/AddressRange";
import { SubnetSelect } from "../../components/SubnetSelect/SubnetSelect";
import { VNETSelect } from "../../components/VNETSelect/VNETSelect";
import { vnetTroubleshooting } from "../../constants/docConstants";
import { IdResourceType } from "../../helpers/idHelper";
import { ACCESS_TYPES, AdbWorkloadType } from "../../helpers/resourceHelper";
import { useFeatures } from "../../hooks/useFeatures";

export const GROUP_NETWORK = "network";

const classNames = mergeStyleSets({
  separator: { padding: "2px 8px" },
  address: { width: "40%", minWidth: "107px", marginRight: "10px" },
  addressRange: { width: "60%", minWidth: "107px" },
});

export const enum Fields {
  Address = "address",
  AddressRange = "addressRange",
  IPsCidrs = "adbsDatabaseDetails.whitelistedIps",
  AccessType = "adbsDatabaseDetails.isAccessControlEnabled",
  MTLSAuthentication = "adbsDatabaseDetails.isMtlsConnectionRequired",
  VNET = "adbsDatabaseDetails.vnetId",
  Subnet = "adbsDatabaseDetails.subnetId",
  RouteVNET = "routeVNET"
}

export enum FieldTestIds {
  VnetSelect = "vnet-select",
  // eslint-disable-next-line @typescript-eslint/no-shadow
  SubnetSelect = "subnet-select",
  DirectConnectionCb = "direct-connection-cb",
  AccessTypeEveryWhere = "access-type-everywhere",
  AccessTypeIpAddress = "access-type-ip-address",
  // eslint-disable-next-line @typescript-eslint/no-shadow
  IpInput = "ip-input",
}

const accessTypes = (): RadioGroupOption[] => ([
  {
    id: ACCESS_TYPES.OPEN,
    text: Messages.labels.accessFromEverywhere(),
    testId: FieldTestIds.AccessTypeEveryWhere,
  },
  {
    id: ACCESS_TYPES.RESTRICTED,
    text: Messages.labels.accessFromAllowedIPs(),
    testId: FieldTestIds.AccessTypeIpAddress,
  },
] as RadioGroupOption[]);

const accessTypeHints = (accessFromEverywhere: JSX.Element, accessFromAllowedIPs: JSX.Element): JSX.Element => (
  <>
    {accessFromEverywhere}
    <br />
    {accessFromAllowedIPs}
  </>
);

interface AddressRange {
  [Fields.Address]: string;
  [Fields.AddressRange]: string;
}

export interface NetworkTabContentProps {
  inputWizardPanelRef: InputWizardPanelComponent;
  subscription: string;
  location: string;
  workload?: AdbWorkloadType;
  isApex: boolean;
}

export const NetworkTabContent = (
  { inputWizardPanelRef, subscription, location, workload, isApex }: NetworkTabContentProps,
): JSX.Element => {
  const { enableAdbsPE } = useFeatures();
  const [simpleBuilderkey, setSimpleBuilderKey] = React.useState<string>(uniqueGUID());
  const [requireAuthKey, setRequireAuthKey] = React.useState<string>(uniqueGUID());
  const [defaultValueRequireAuth, setDefaultValueRequireAuth] = React.useState<boolean>(true);
  const [disableRequireAuth, setDisableRequireAuth] = React.useState<boolean>(true);
  const [showIPsCidrs, setShowIPsCidrs] = React.useState<boolean>(false);
  const [showRouteVNET, setShowRouteVNET] = React.useState<boolean>(true);
  const [isRouteVNET, setIsRouteVNET] = React.useState<boolean>(true);
  const [selectedVnet, setSelectedVnet] = React.useState<string>();
  const [selectedSubnet, setSelectedSubnet] = React.useState<string>();
  const [showVnetAndSubNet, setShowVnetAndSubNet] = React.useState<boolean>(true);
  const accessOptions = accessTypes();
  const [selectedAccessOptions, setSelectedAccessOptions] = React.useState<string>(accessOptions[0].id);
  const accessFromEverywhere = FormattedString({ inputText: Messages.hints.accessFromEverywhere() });
  const accessFromAllowedIPs = FormattedString(
    { inputText: Messages.hints.accessFromAllowedIPs() },
  );
  const onError = (): void => inputWizardPanelRef.showError(Messages.createCommon.loadingErrors.general());
  const onMissingDependencies = (missingDependencies: IdResourceType[]): void => {
    const dependencyType = missingDependencies?.[0];
    if (dependencyType === IdResourceType.SUBSCRIPTIONS || dependencyType === IdResourceType.LOCATION
      || dependencyType === IdResourceType.RESOURCE_GROUPS) {
      inputWizardPanelRef.showErrorDialog(
        Messages.createCommon.dependencyWarnings.basicsIncomplete.message(),
        Messages.createCommon.dependencyWarnings.basicsIncomplete.title(),
      );
    } else if (dependencyType === IdResourceType.VNETS) {
      inputWizardPanelRef.showErrorDialog(
        Messages.createCommon.dependencyWarnings.vnetNotSelected.message(),
        Messages.createCommon.dependencyWarnings.vnetNotSelected.title(),
      );
    }
  };
  const onChangeVnet = (id: string): void => {
    setSelectedVnet(id);
  };
  const onChangeSubnet = (id: string): void => {
    setSelectedSubnet(id);
  };

  const vnetTooltip = FormattedString(
    { inputText: Messages.hints.tooltipVNET(vnetTroubleshooting) },
  ) as unknown as string;

  React.useMemo(() => {
    setSelectedVnet(undefined);
    setSelectedSubnet(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription, location]);

  React.useEffect(() => {
    if (selectedAccessOptions === accessOptions[0].id) {
      setShowIPsCidrs(false);
      setRequireAuthKey(uniqueGUID());
      setDefaultValueRequireAuth(true);
      setDisableRequireAuth(true);
      if (isRouteVNET) {
        setShowVnetAndSubNet(true);
      }
      setShowRouteVNET(true);
      setSimpleBuilderKey(uniqueGUID());
    } else {
      setShowIPsCidrs(true);
      setDisableRequireAuth(false);
      setShowRouteVNET(false);
      setShowVnetAndSubNet(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccessOptions]);
  const form: FormState = React.useContext(FormContext);

  const ipsCidrs = form?.getValue(Fields.IPsCidrs, GROUP_NETWORK) as Record<string, string>[];
  const defaultIpsCidrs = (ipsCidrs?.filter(value => !!value[Fields.Address]) ?? [])
    .map(value => ({
      [Fields.Address]: value[Fields.Address],
      [Fields.AddressRange]: value[Fields.Address],
    }));

  const [networkAddresses, setNetworkAddresses] = React.useState<AddressRange[]>(defaultIpsCidrs);

  const onAddressChange = (): void => {
    const values = form?.getValuesByFieldPrefix(`${Fields.Address}-`, GROUP_NETWORK);
    const addresses = Object.values(values);
    setNetworkAddresses(addresses.map(cidr => ({ [Fields.Address]: cidr, [Fields.AddressRange]: cidr })));
  };

  return (
    <FieldSet header="">
      <RadioGroup
        fieldName={Fields.AccessType}
        label={Messages.labels.accessType()}
        options={accessOptions}
        defaultValue={accessOptions[0].id}
        statusInfo={{
          messageType: InfoBlockStatus.INFO,
          infoLayout: InfoBlockLayout.Compact,
          message: accessTypeHints(accessFromEverywhere, accessFromAllowedIPs),
        }}
        onChange={(option: string) => setSelectedAccessOptions(option)}
      />
      {showRouteVNET && enableAdbsPE && (
        <CheckBox
          fieldName={Fields.RouteVNET}
          testId={FieldTestIds.DirectConnectionCb}
          defaultValue={isRouteVNET}
          label={Messages.labels.routeVNET()}
          statusInfo={{
            messageType: InfoBlockStatus.INFO,
            infoLayout: InfoBlockLayout.Compact,
            message: Messages.hints.routeVNET(),
          }}
          onChange={(checked: boolean) => {
            setShowVnetAndSubNet(checked);
            setIsRouteVNET(checked);
          }}
        />
      )}
      {showIPsCidrs && (
      <FieldWrapper
        key={simpleBuilderkey}
        label={Messages.labels.ipsAndOrCidrs()}
        tooltip={Messages.hints.ipsAndOrCidrs()}
        required
      >
        <SimpleBuilder
          defaultValue={networkAddresses}
          fieldName={Fields.IPsCidrs}
          minRows={1}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          validator={((values: { [key: string]: any; }[]) => {
            const errors: string[] = [];

            const isRequired = values?.every(ele => !ele?.[Fields.Address]);
            if (!values || !values?.length || isRequired) {
              errors.push(Messages.validation.required());
            } else {
              const addresses = values.map(ele => ele?.[Fields.Address]);
              const overlaps = getOverlapCidrs(addresses);
              // eslint-disable-next-line max-len
              overlaps.forEach(overlap => errors.push(Messages.validation.addressOverlap(overlap?.cidr1, overlap?.cidr2)));
            }
            return errors.length ? errors : undefined;
          })}
        >
          <IpInput
            label={Messages.labels.address()}
            fieldName={Fields.Address}
            testId={FieldTestIds.IpInput}
            className={classNames.address}
            onChange={onAddressChange}
            validation={{
              containsCidrBlock: true,
              containsIpAddress: true,
            }}
          />
          <AddressRangeDisplay
            label={Messages.labels.addressRange()}
            fieldName={Fields.AddressRange}
            groupName={GROUP_NETWORK}
            className={classNames.addressRange}
          />
        </SimpleBuilder>
      </FieldWrapper>
      )}
      {showVnetAndSubNet && enableAdbsPE && (
        <>
          <VNETSelect
            required
            defaultValue={selectedVnet ? [selectedVnet] : undefined}
            fieldName={Fields.VNET}
            label={Messages.labels.virtualNetwork()}
            testId={FieldTestIds.VnetSelect}
            tooltip={vnetTooltip}
            subscriptionId={subscription}
            location={location}
            onChange={onChangeVnet}
            onError={onError}
            onMissingDependencies={onMissingDependencies}
          />
          <SubnetSelect
            required
            fieldName={Fields.Subnet}
            defaultValue={selectedSubnet ? [selectedSubnet] : undefined}
            label={Messages.labels.subnet()}
            testId={FieldTestIds.SubnetSelect}
            vnetId={selectedVnet}
            subscriptionId={subscription}
            location={location}
            onError={onError}
            onMissingDependencies={onMissingDependencies}
            onChange={onChangeSubnet}
          />
        </>
      )}
      {!isApex && workload !== AdbWorkloadType.APEX && (
        <CheckBox
          key={requireAuthKey}
          defaultValue={defaultValueRequireAuth}
          disabled={disableRequireAuth}
          fieldName={Fields.MTLSAuthentication}
          label={Messages.labels.requiretMTLSAuthentication()}
          statusInfo={{
            messageType: InfoBlockStatus.INFO,
            infoLayout: InfoBlockLayout.Compact,
            message: Messages.hints.requiretMTLSAuthentication(),
          }}
        />
      )}
    </FieldSet>
  );
};
